<script setup>
import Card from "primevue/card"
import DataTable from "primevue/datatable"
import Column from "primevue/column"
import {fancyDateShort} from "@/filters/fancyDate.js";
import InputText from "primevue/inputtext";
import InputIcon from "primevue/inputicon";
import IconField from "primevue/iconfield";
import {computed, watch} from "vue";
import {useServerTable} from "@/composables/useServerTable";
import {useContractsStore} from "@/stores/contract.js";
import Button from 'primevue/button';
import { ref } from 'vue';
import ContractForm from '@/components/contract/ContractForm.vue';
import { useRouter } from 'vue-router';



const props = defineProps({
  // Один из идентификаторов обязателен: customerId или facilityId
  customerId: {
    type: [Number, String],
    required: false
  },
  facilityId: {
    type: [Number, String],
    required: false
  },
  workId: { type: [Number, String], required: false, default: undefined },
  rowsPerPage: { type: Number, default: 3 },
});

const store = useContractsStore();
const showControls = computed(() => loading.value || totalRecords.value > 0);

const {
  data: contracts,
  totalRecords,
  loading,
  rowsPerPage,
  onPageChange,
  setSearch,
  setExtra,
  firstIndex
} = useServerTable({
  initialRows: props.rowsPerPage,
  debounceMs: 500,
  // Простая передача параметров в стор, как в Contacts.vue
  loadPage: params => store.loadContractsByTarget(params),
});

watch(
    () => [props.customerId, props.facilityId, props.workId],
    ([customerId, facilityId, workId]) => {
      const target = customerId != null ? 'customer' : facilityId != null ? 'facility' : workId != null ? 'work' : null;
      const id = customerId ?? facilityId ?? workId ?? null;
      if (target && id != null) {
        setExtra({ target, id });
      }
    },
    { immediate: true }
);

const showCreateDialog = ref(false);
const router = useRouter();

function openCreate() {
  showCreateDialog.value = true;
}
function onCreated(contract) {
  if (contract?.id) {
    router.push(`/contracts/${contract.id}`);
  }
}


</script>


<template>
  <Card>
    <template #title>
      <div class="w-full flex items-center justify-between px-4 gap-3 text-lg">
        <span>Договоры</span>
        <IconField class="pt-2" v-if="showControls">
          <InputIcon class="pi pi-search pt-1" />
          <InputText placeholder="Поиск договоров..." size="small" @update:modelValue="setSearch" class="h-7"/>
        </IconField>
        <Button
            v-if="facilityId"
            size="small"
            icon="pi pi-plus"
            rounded
            text
            @click="openCreate"
        />

      </div>
    </template>
    <template #content>
      <DataTable
          :value="contracts"
          lazy
          :paginator="totalRecords > rowsPerPage"
      :rows="rowsPerPage"
      :totalRecords="totalRecords"
      :loading="loading"
      :first="firstIndex()"
      @page="onPageChange"
      class="text-sm"
      paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
      currentPageReportTemplate="от {first} до {last} из {totalRecords}"
      emptyMessage="Договоров нет"

      >
      <Column>
          <template #body="{ data }">
            <router-link :to="`/contracts/${data.id}`">
              <p class="flex items-center">
                <i class="pi pi-folder text-xs text-gray-500 mr-2 align-middle"></i>
                {{ `Договор №${data.number} от ${fancyDateShort(data.date)}` }}
              </p>
            </router-link>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>
  <ContractForm
      v-if="facilityId"
      v-model:visible="showCreateDialog"
      :preset-facility-id="Number(facilityId)"
      @created="onCreated"
  />

</template>


<style scoped>

:deep(.p-datatable-thead > tr > th),
:deep(.p-datatable-tbody > tr > td) {
  border: none !important;
  background: transparent !important;
  border-bottom: none !important;
}

:deep(.p-datatable-tbody > tr > td) {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

:deep(.p-datatable-thead > tr > th) {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

:deep(.p-paginator .p-paginator-prev),
:deep(.p-paginator .p-paginator-next) {
  height: 2rem;
  min-width: 2rem;

}

:deep(.p-card-body) {
  padding: 2px 0 8px;
}
</style>